import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TopBar from "@/components/TopBar";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  MessageSquare, Search, Send, Paperclip, X, File as FileIcon,
  Image as ImageIcon, ArrowLeft, Plus,
} from "lucide-react";

const POLL_MS = 15000;
const MAX_MB = 25;

function relTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function Messaging() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [showNewDlg, setShowNewDlg] = useState(false);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const meId = user?.id;

  // Poll-driven loaders log quietly; only the first load surfaces a toast to avoid spamming every 15s.
  const loadThreads = useCallback(async (notify = false) => {
    try {
      const { data } = await api.get("/messaging/threads");
      setThreads(data);
    } catch (e) {
      console.error("Failed to load threads", e);
      if (notify) toast.error("Failed to load conversations. Please try again.");
    }
  }, []);

  const loadMessages = useCallback(async (threadId, notify = false) => {
    if (!threadId) return;
    try {
      const { data } = await api.get(`/messaging/threads/${threadId}/messages`);
      setMessages(data);
      api.post(`/messaging/threads/${threadId}/read`).then(() => loadThreads())
        .catch((e) => console.error("Failed to mark thread read", e));
    } catch (e) {
      console.error("Failed to load messages", e);
      if (notify) toast.error("Failed to load messages. Please try again.");
    }
  }, [loadThreads]);

  const loadStaff = useCallback(async () => {
    try {
      const { data } = await api.get("/messaging/staff");
      setStaff(data);
    } catch (e) {
      console.error("Failed to load staff", e);
      toast.error("Failed to load staff list. Please try again.");
    }
  }, []);

  // Initial load
  useEffect(() => {
    (async () => {
      await Promise.all([loadThreads(true), loadStaff()]);
      setLoading(false);
    })();
  }, [loadThreads, loadStaff]);

  // Load messages when active thread changes
  useEffect(() => { loadMessages(activeId, true); }, [activeId, loadMessages]);

  // Poll every 15s
  useEffect(() => {
    const t = setInterval(() => {
      loadThreads();
      if (activeId) loadMessages(activeId);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [activeId, loadThreads, loadMessages]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, activeId]);

  const openThreadWith = async (partnerId) => {
    try {
      const { data } = await api.post("/messaging/threads", { partner_user_id: partnerId });
      setShowNewDlg(false);
      await loadThreads();
      setActiveId(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open thread");
    }
  };

  const filteredThreads = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return threads;
    return threads.filter((t) => (t.other_user?.name || "").toLowerCase().includes(term)
      || (t.last_message_preview || "").toLowerCase().includes(term));
  }, [threads, q]);

  const activeThread = threads.find((t) => t.id === activeId);

  return (
    <div data-testid="messaging-page">
      <TopBar
        title="Messaging"
        subtitle="Staff direct messages"
        actions={
          <Button
            onClick={() => setShowNewDlg(true)}
            data-testid="new-dm-btn"
            className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-10 text-[11px] font-condensed uppercase tracking-widest"
          >
            <Plus className="w-3 h-3 mr-1" /> New DM
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] h-[calc(100vh-72px)]">
        {/* Threads list */}
        <aside className={`bg-surface border-r border-surface-3 flex flex-col ${activeId ? "hidden md:flex" : "flex"}`} data-testid="threads-panel">
          <div className="p-3 border-b border-surface-3">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="threads-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search DMs…"
                className="pl-10 bg-background border-surface-3 rounded-none h-9 text-sm"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-sm text-slate-500">Loading…</div>
            ) : filteredThreads.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 italic" data-testid="threads-empty">
                {q ? "No matching DMs." : "No conversations yet. Hit New DM to start one."}
              </div>
            ) : (
              filteredThreads.map((t) => {
                const other = t.other_user;
                const active = t.id === activeId;
                const unread = t.unread_count > 0;
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveId(t.id)}
                    data-testid={`thread-${t.id}`}
                    className={`w-full text-left px-4 py-3 border-b border-surface-3 transition-colors ${
                      active ? "bg-amber/10 border-l-2 border-l-amber" : "hover:bg-surface-2"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar name={other?.name} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className={`font-semibold text-sm truncate ${unread ? "text-amber" : ""}`}>
                            {other?.name || "Unknown"}
                          </div>
                          <div className="text-[10px] text-slate-500 shrink-0">{relTime(t.last_message_at)}</div>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                          <div className="text-xs text-slate-400 truncate">
                            {t.last_message_sender_id === meId && "You: "}
                            {t.last_message_preview || <span className="italic text-slate-600">No messages yet</span>}
                          </div>
                          {unread && (
                            <span
                              className="shrink-0 bg-amber text-[#0B0D12] text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center"
                              data-testid={`unread-${t.id}`}
                            >
                              {t.unread_count}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 mt-1 truncate">
                          {(other?.title || other?.role || "").replace(/_/g, " ")}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Message pane */}
        <section className={`flex flex-col bg-background ${activeId ? "flex" : "hidden md:flex"}`} data-testid="message-panel">
          {!activeThread ? (
            <div className="flex-1 flex items-center justify-center text-center px-8">
              <div>
                <MessageSquare className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <div className="font-display text-xl uppercase tracking-wider text-slate-400">Pick a conversation</div>
                <div className="text-sm text-slate-500 mt-2">Or start a new DM to reach any coach on your staff.</div>
              </div>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="px-6 py-3 border-b border-surface-3 flex items-center gap-3 bg-surface">
                <button
                  onClick={() => setActiveId(null)}
                  data-testid="thread-back"
                  className="md:hidden text-slate-400 hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <Avatar name={activeThread.other_user?.name} />
                <div className="flex-1">
                  <div className="font-semibold text-sm">{activeThread.other_user?.name}</div>
                  <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">
                    {(activeThread.other_user?.title || activeThread.other_user?.role || "").replace(/_/g, " ")}
                    {activeThread.other_user?.email && ` · ${activeThread.other_user.email}`}
                  </div>
                </div>
              </div>

              {/* Message list */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-2" data-testid="message-list">
                {messages.length === 0 ? (
                  <div className="text-center text-sm text-slate-500 py-16 italic">
                    Say hi to {activeThread.other_user?.name?.split(" ")[0]}. Messages will show up here.
                  </div>
                ) : (
                  messages.map((m, i) => (
                    <MessageRow
                      key={m.id}
                      m={m}
                      meId={meId}
                      prev={messages[i - 1]}
                    />
                  ))
                )}
              </div>

              {/* Composer */}
              <Composer
                threadId={activeThread.id}
                onSent={() => { loadMessages(activeThread.id); loadThreads(); }}
              />
            </>
          )}
        </section>
      </div>

      {showNewDlg && (
        <NewDMDialog
          staff={staff}
          onClose={() => setShowNewDlg(false)}
          onOpen={openThreadWith}
        />
      )}
    </div>
  );
}

function Avatar({ name }) {
  const initials = (name || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div className="w-9 h-9 bg-surface-2 border border-surface-3 flex items-center justify-center font-display text-sm text-amber shrink-0">
      {initials}
    </div>
  );
}

function MessageRow({ m, meId, prev }) {
  const mine = m.sender_id === meId;
  const showHeader = !prev || prev.sender_id !== m.sender_id
    || (new Date(m.created_at).getTime() - new Date(prev.created_at).getTime()) > 5 * 60_000;

  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
      <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
        {showHeader && (
          <div className={`text-[10px] font-condensed uppercase tracking-widest text-slate-500 mb-1 ${mine ? "text-right" : ""}`}>
            {mine ? "You" : m.sender_name} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        )}
        {m.body && (
          <div
            className={`px-4 py-2 whitespace-pre-wrap break-words text-sm ${
              mine
                ? "bg-amber text-[#0B0D12]"
                : "bg-surface text-slate-100 border border-surface-3"
            }`}
          >
            {m.body}
          </div>
        )}
        {(m.attachments || []).map((a) => (
          <AttachmentTile key={a.id} a={a} mine={mine} />
        ))}
      </div>
    </div>
  );
}

function AttachmentTile({ a, mine }) {
  const isImage = (a.mime || "").startsWith("image/");
  const href = `${BACKEND_URL}${a.url}`;
  if (isImage) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={`mt-1 border border-surface-3 max-w-xs block ${mine ? "self-end" : ""}`}>
        <img src={href} alt={a.name} className="max-h-64 object-contain block" />
        <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 px-2 py-1 bg-surface border-t border-surface-3">
          {a.name} · {fmtBytes(a.size)}
        </div>
      </a>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`mt-1 border border-surface-3 bg-surface flex items-center gap-3 px-3 py-2 hover:border-amber transition-colors max-w-xs ${mine ? "self-end" : ""}`}
      data-testid={`attach-${a.id}`}
    >
      <FileIcon className="w-4 h-4 text-amber shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate">{a.name}</div>
        <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">{fmtBytes(a.size)}</div>
      </div>
    </a>
  );
}

function Composer({ threadId, onSent }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const attachFiles = (fl) => {
    const arr = Array.from(fl || []);
    for (const f of arr) {
      if (f.size / (1024 * 1024) > MAX_MB) {
        toast.error(`${f.name} is over ${MAX_MB}MB`);
        return;
      }
    }
    setFiles([...files, ...arr]);
  };

  const send = async () => {
    if (!text.trim() && files.length === 0) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("body", text);
      for (const f of files) fd.append("files", f);
      await api.post(`/messaging/threads/${threadId}/messages`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setText("");
      setFiles([]);
      onSent && onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="border-t border-surface-3 bg-surface p-3" data-testid="composer">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2" data-testid="composer-attachments">
          {files.map((f, i) => (
            <div key={`${f.name}-${f.size}-${f.lastModified}`} className="flex items-center gap-2 bg-background border border-surface-3 px-2 py-1 text-xs">
              {f.type.startsWith("image/") ? <ImageIcon className="w-3 h-3 text-amber" /> : <FileIcon className="w-3 h-3 text-amber" />}
              <span className="truncate max-w-[180px]">{f.name}</span>
              <span className="text-slate-500 text-[10px]">{fmtBytes(f.size)}</span>
              <button
                onClick={() => setFiles(files.filter((_, j) => j !== i))}
                data-testid={`remove-attach-${i}`}
                className="text-slate-500 hover:text-red-400"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <input
          ref={fileRef}
          type="file"
          multiple
          onChange={(e) => { attachFiles(e.target.files); e.target.value = ""; }}
          className="hidden"
          data-testid="file-input"
        />
        <button
          onClick={() => fileRef.current?.click()}
          data-testid="attach-btn"
          className="text-slate-400 hover:text-amber p-2"
          title="Attach files (max 25MB each)"
        >
          <Paperclip className="w-4 h-4" />
        </button>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder="Write a message… (Enter to send, Shift+Enter for newline)"
          rows={1}
          data-testid="composer-input"
          className="flex-1 bg-background border border-surface-3 text-sm px-3 py-2 focus:outline-none focus:border-amber resize-none min-h-[40px] max-h-40"
        />
        <button
          onClick={send}
          disabled={sending || (!text.trim() && files.length === 0)}
          data-testid="send-btn"
          className="bg-amber hover:bg-amber-600 text-[#0B0D12] h-10 px-4 text-[11px] font-condensed uppercase tracking-widest disabled:opacity-40 flex items-center gap-1"
        >
          <Send className="w-3 h-3" /> {sending ? "…" : "Send"}
        </button>
      </div>
    </div>
  );
}

function NewDMDialog({ staff, onClose, onOpen }) {
  const [q, setQ] = useState("");
  const filtered = staff.filter((s) => {
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return (s.name || "").toLowerCase().includes(t)
      || (s.email || "").toLowerCase().includes(t)
      || (s.title || "").toLowerCase().includes(t);
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 pt-20" data-testid="new-dm-dialog">
      <div className="bg-background border border-surface-3 max-w-md w-full max-h-[70vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-surface-3 flex items-center justify-between">
          <div className="font-display text-xl uppercase tracking-wider">Start a DM</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="new-dm-close">✕</button>
        </div>
        <div className="p-3 border-b border-surface-3">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search staff…"
              autoFocus
              className="pl-10 bg-surface border-surface-3 rounded-none h-9 text-sm"
              data-testid="new-dm-search"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 italic">No staff match that search.</div>
          ) : (
            filtered.map((s) => (
              <button
                key={s.id}
                onClick={() => onOpen(s.id)}
                data-testid={`new-dm-staff-${s.id}`}
                className="w-full text-left px-4 py-3 border-b border-surface-3 hover:bg-surface flex items-center gap-3"
              >
                <Avatar name={s.name} />
                <div>
                  <div className="text-sm font-semibold">{s.name}</div>
                  <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">
                    {(s.title || s.role || "").replace(/_/g, " ")} · {s.email}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
